import React from 'react'
import { useTranslation } from 'react-i18next'
import { PublicNavigationBar } from '../components/PublicNavigationBar'

// Import the terms of service content
const enTermsContent = `# Terms of Service - The Ranch Summer Camp Web Application

**Last Updated: March 23, 2025**

## 1. Introduction

Welcome to The Ranch ("we," "our," or "us") web application. This platform allows parents and guardians to register children and make bookings for summer camp periods. By accessing or using our web application (the "Service"), you agree to be bound by these Terms of Service ("Terms"). Please read these Terms carefully before using our Service.

## 2. Acceptance of Terms

By accessing or using our Service, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use our Service. By registering children for our summer camp through this Service, you confirm that you are the legal parent or guardian of those children or have proper authorization from their legal parent or guardian.

## 3. Changes to Terms

We reserve the right to modify these Terms at any time. We will provide notice of any material changes by posting the updated Terms on our website or through other communications. Your continued use of the Service after such changes constitutes your acceptance of the new Terms.

## 4. User Accounts

### 4.1 Registration
To access our Service, you must register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.

### 4.2 Account Security
You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to immediately notify us of any unauthorized use of your account or any other breach of security.

### 4.3 Child Registration
When registering children for our summer camp, you must provide accurate and complete information about each child, including but not limited to medical information, emergency contacts, and special needs. You acknowledge that providing false or incomplete information may put a child's health and safety at risk.

## 5. Booking and Payment

### 5.1 Booking Process
The Service allows you to book summer camp periods for children. All bookings are subject to availability and confirmation.

### 5.2 Payment
Payment terms are as specified during the booking process. Prices are subject to change until booking is confirmed. All payments are processed securely through our authorized payment processors.

### 5.3 Cancellation and Refunds
Cancellation and refund policies are as specified during the booking process. Please note that certain booking periods may have different cancellation policies.

## 6. User Conduct

You agree not to use our Service to:

- Violate any applicable laws or regulations
- Infringe upon the rights of others
- Submit false or misleading information
- Harass, abuse, or harm another person
- Transmit any harmful code or material
- Interfere with the proper functioning of our Service
- Engage in any activity that could disable, overburden, or impair our Service

## 7. Intellectual Property

### 7.1 Our Intellectual Property
Our Service and all content, features, and functionality thereof, including but not limited to text, graphics, logos, icons, images, audio clips, digital downloads, data compilations, and software, are the property of The Ranch or our licensors and are protected by copyright, trademark, and other intellectual property laws.

### 7.2 User Content
You retain ownership of any content you submit, post, or display on or through our Service ("User Content"). By submitting User Content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate, distribute, and display such User Content in connection with providing our Service.

## 8. Privacy and Data Protection

### 8.1 Privacy Policy
Your use of our Service is also governed by our Privacy Policy, which is incorporated into these Terms by reference. Our Privacy Policy details how we collect, use, and safeguard the information provided by you, including personal data of children.

### 8.2 Data Protection
We comply with all applicable data protection laws, including the General Data Protection Regulation (GDPR) and local Greek data protection laws. We implement appropriate technical and organizational measures to protect personal data against unauthorized or unlawful processing and against accidental loss, destruction, or damage.

### 8.3 Children's Data
We collect and process children's personal data solely for the purpose of providing our summer camp services and ensuring their health, safety, and well-being. By registering a child through our Service, you consent to our collection and processing of the child's personal data for these purposes.

## 9. Health and Safety

### 9.1 Medical Information
You must provide accurate and complete medical information about each registered child, including but not limited to allergies, medications, medical conditions, and vaccination status. Failure to disclose relevant medical information may result in the child being denied participation in camp activities or being sent home.

### 9.2 Emergency Authorization
By registering a child for our summer camp, you authorize us to seek medical treatment for the child in case of emergency when you cannot be reached immediately.

## 10. Third-Party Links and Services

Our Service may contain links to third-party websites or services that are not owned or controlled by us. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services.

## 11. Termination

We reserve the right to suspend or terminate your access to our Service, without prior notice or liability, for any reason, including but not limited to a breach of these Terms.

## 12. Disclaimer of Warranties

THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.

## 13. Limitation of Liability

IN NO EVENT SHALL THE RANCH, ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES, BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE.

## 14. Indemnification

You agree to defend, indemnify, and hold harmless The Ranch and its licensees and licensors, and their employees, contractors, agents, officers, and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses.

## 15. Governing Law

These Terms shall be governed by and construed in accordance with the laws of Greece, without regard to its conflict of law provisions.

## 16. Dispute Resolution

Any dispute arising from or relating to the subject matter of these Terms shall be subject to the exclusive jurisdiction of the courts of Corinth, Greece.

## 17. Severability

If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law and the remaining provisions will continue in full force and effect.

## 18. Entire Agreement

These Terms constitute the entire agreement between you and The Ranch regarding our Service, and supersede and replace any prior agreements we might have had between us regarding the Service.

## 19. Contact Information

If you have any questions about these Terms, please contact us at:

The Ranch
Corinth, Greece
Email: info@ranch.gr
Phone: +30 2741 0 93801
Website: https://ranch.gr/en`

const elTermsContent = `# Όροι Χρήσης - Διαδικτυακή Εφαρμογή Παιδικής Κατασκήνωσης The Ranch

**Τελευταία Ενημέρωση: 23 Μαρτίου 2025**

## 1. Εισαγωγή

Καλώς ήρθατε στη διαδικτυακή εφαρμογή του The Ranch («εμείς», «μας», ή «μας»). Αυτή η πλατφόρμα επιτρέπει στους γονείς και κηδεμόνες να εγγράφουν παιδιά και να κάνουν κρατήσεις για περιόδους θερινής κατασκήνωσης. Με την πρόσβαση ή τη χρήση της διαδικτυακής μας εφαρμογής (η «Υπηρεσία»), συμφωνείτε να δεσμεύεστε από αυτούς τους Όρους Χρήσης («Όροι»). Παρακαλούμε διαβάστε προσεκτικά αυτούς τους Όρους πριν χρησιμοποιήσετε την Υπηρεσία μας.

## 2. Αποδοχή των Όρων

Με την πρόσβαση ή χρήση της Υπηρεσίας μας, αναγνωρίζετε ότι έχετε διαβάσει, κατανοήσει και συμφωνείτε να δεσμεύεστε από αυτούς τους Όρους. Εάν δεν συμφωνείτε με αυτούς τους Όρους, δεν επιτρέπεται να αποκτήσετε πρόσβαση ή να χρησιμοποιήσετε την Υπηρεσία μας. Με την εγγραφή παιδιών στην κατασκήνωσή μας μέσω αυτής της Υπηρεσίας, επιβεβαιώνετε ότι είστε ο νόμιμος γονέας ή κηδεμόνας αυτών των παιδιών ή έχετε κατάλληλη εξουσιοδότηση από τον νόμιμο γονέα ή κηδεμόνα τους.

## 3. Αλλαγές στους Όρους

Διατηρούμε το δικαίωμα να τροποποιήσουμε αυτούς τους Όρους ανά πάσα στιγμή. Θα παρέχουμε ειδοποίηση για τυχόν ουσιώδεις αλλαγές αναρτώντας τους ενημερωμένους Όρους στον ιστότοπό μας ή μέσω άλλων επικοινωνιών. Η συνεχιζόμενη χρήση της Υπηρεσίας μετά από τέτοιες αλλαγές αποτελεί την αποδοχή σας των νέων Όρων.

## 4. Λογαριασμοί Χρηστών

### 4.1 Εγγραφή
Για να αποκτήσετε πρόσβαση στην Υπηρεσία μας, πρέπει να εγγραφείτε για λογαριασμό. Συμφωνείτε να παρέχετε ακριβείς, τρέχουσες και πλήρεις πληροφορίες κατά τη διαδικασία εγγραφής και να ενημερώνετε αυτές τις πληροφορίες για να τις διατηρείτε ακριβείς, τρέχουσες και πλήρεις.

### 4.2 Ασφάλεια Λογαριασμού
Είστε υπεύθυνοι για τη διατήρηση της εμπιστευτικότητας των διαπιστευτηρίων του λογαριασμού σας και για όλες τις δραστηριότητες που συμβαίνουν στο λογαριασμό σας. Συμφωνείτε να μας ειδοποιήσετε αμέσως για οποιαδήποτε μη εξουσιοδοτημένη χρήση του λογαριασμού σας ή οποιαδήποτε άλλη παραβίαση ασφαλείας.

### 4.3 Εγγραφή Παιδιών
Κατά την εγγραφή παιδιών στην κατασκήνωσή μας, πρέπει να παρέχετε ακριβείς και πλήρεις πληροφορίες για κάθε παιδί, συμπεριλαμβανομένων, μεταξύ άλλων, ιατρικών πληροφοριών, επαφών έκτακτης ανάγκης και ειδικών αναγκών. Αναγνωρίζετε ότι η παροχή ψευδών ή ελλιπών πληροφοριών μπορεί να θέσει σε κίνδυνο την υγεία και την ασφάλεια ενός παιδιού.

## 5. Κρατήσεις και Πληρωμές

### 5.1 Διαδικασία Κράτησης
Η Υπηρεσία σας επιτρέπει να κάνετε κρατήσεις για περιόδους θερινής κατασκήνωσης για παιδιά. Όλες οι κρατήσεις υπόκεινται σε διαθεσιμότητα και επιβεβαίωση.

### 5.2 Πληρωμή
Οι όροι πληρωμής καθορίζονται κατά τη διαδικασία κράτησης. Οι τιμές μπορεί να αλλάξουν μέχρι να επιβεβαιωθεί η κράτηση. Όλες οι πληρωμές γίνονται με ασφάλεια μέσω των εξουσιοδοτημένων επεξεργαστών πληρωμών μας.

### 5.3 Ακύρωση και Επιστροφή Χρημάτων
Οι πολιτικές ακύρωσης και επιστροφής χρημάτων καθορίζονται κατά τη διαδικασία κράτησης. Παρακαλούμε σημειώστε ότι ορισμένες περίοδοι κράτησης μπορεί να έχουν διαφορετικές πολιτικές ακύρωσης.

## 6. Συμπεριφορά Χρήστη

Συμφωνείτε να μην χρησιμοποιείτε την Υπηρεσία μας για:

- Παραβίαση οποιωνδήποτε ισχυόντων νόμων ή κανονισμών
- Παραβίαση των δικαιωμάτων άλλων
- Υποβολή ψευδών ή παραπλανητικών πληροφοριών
- Παρενόχληση, κακοποίηση ή βλάβη άλλου προσώπου
- Μετάδοση οποιουδήποτε επιβλαβούς κώδικα ή υλικού
- Παρέμβαση στην καλή λειτουργία της Υπηρεσίας μας
- Συμμετοχή σε οποιαδήποτε δραστηριότητα που θα μπορούσε να απενεργοποιήσει, να υπερφορτώσει ή να βλάψει την Υπηρεσία μας

## 7. Πνευματική Ιδιοκτησία

### 7.1 Η Πνευματική μας Ιδιοκτησία
Η Υπηρεσία μας και όλο το περιεχόμενο, τα χαρακτηριστικά και η λειτουργικότητά της, συμπεριλαμβανομένων, αλλά όχι περιοριστικά, κειμένου, γραφικών, λογότυπων, εικονιδίων, εικόνων, ηχητικών κλιπ, ψηφιακών λήψεων, συγκεντρώσεων δεδομένων και λογισμικού, είναι ιδιοκτησία του The Ranch ή των αδειοδοτών μας και προστατεύονται από νόμους περί πνευματικής ιδιοκτησίας, εμπορικών σημάτων και άλλης πνευματικής ιδιοκτησίας.`

export function TermsOfServicePage() {
  const { t, i18n } = useTranslation(['common'])
  const currentLanguage = i18n.language

  // Choose content based on current language
  const termsContent = currentLanguage === 'el' ? elTermsContent : enTermsContent

  return (
    <div className="home-container min-h-screen flex flex-col" style={{ backgroundImage: 'url(/images/background.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
      {/* Navigation Bar */}
      <PublicNavigationBar />

      {/* Main Content */}
      <main className="flex-grow max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white border-2 border-black shadow-brutal p-8">
          <h1 className="text-3xl font-black text-neobrutalism-text mb-6">
            {t('legal.termsOfService')}
          </h1>
          
          <div className="prose prose-lg max-w-none">
            {/* Render markdown content */}
            <div dangerouslySetInnerHTML={{ 
              __html: termsContent.split('\n').map(line => {
                // Handle headings
                if (line.startsWith('# ')) {
                  return `<h1 class="text-2xl font-bold mt-8 mb-4">${line.replace('# ', '')}</h1>`;
                }
                if (line.startsWith('## ')) {
                  return `<h2 class="text-xl font-bold mt-6 mb-3">${line.replace('## ', '')}</h2>`;
                }
                if (line.startsWith('### ')) {
                  return `<h3 class="text-lg font-bold mt-4 mb-2">${line.replace('### ', '')}</h3>`;
                }
                
                // Handle lists
                if (line.startsWith('- ')) {
                  return `<li class="ml-6 mb-2">${line.replace('- ', '')}</li>`;
                }
                
                // Handle paragraphs
                if (line.trim() === '') {
                  return '<br />';
                }
                
                // Handle bold text
                if (line.includes('**')) {
                  return `<p class="mb-4">${line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}</p>`;
                }
                
                return `<p class="mb-4">${line}</p>`;
              }).join('')
            }} />
          </div>
        </div>
      </main>
    </div>
  )
} 